import { useState, useCallback, useMemo } from 'react';
import { FileText, X, Download, Loader2, AlertCircle } from 'lucide-react';
import { captureResumePreviewAsPdf } from '@modules/single-optimize/aiResumeOptimizer/captureResumePreviewAsPdf';
import { stampPdfFooterWatermark } from '@core/utils/stampPdfFooterWatermark';
import { SavedOptimizedResumePreview, buildResumeSectionsFromStoredBody } from './SavedOptimizedResumePreview';
import type { SavedProfileDesign } from '@modules/single-optimize/aiResumeOptimizer/utils/savedProfileResumeFile';

export type OptimizedResumePreviewModalPayload =
  | { kind: 'text'; body: string; templateId: string; design?: SavedProfileDesign }
  | { kind: 'pdf'; url: string };

type Props = {
  open: boolean;
  onClose: () => void;
  headline: string;
  payload: OptimizedResumePreviewModalPayload | null;
  /** When set, shows a themed error panel instead of the preview (payload ignored). */
  error?: string | null;
  /** Short label under the headline when `error` is set. */
  errorSubtitle?: string;
  eyebrow?: string;
  /** Shown at bottom of every PDF page (e.g. organisation / consultancy name). */
  consultancyWatermark?: string | null;
  /** Base name for the downloaded file (without .pdf). */
  downloadFileBase?: string;
};

function sanitizeFileBase(name: string): string {
  const s = name
    .trim()
    .replace(/[<>:"/\\|?*\u0000-\u001f]+/g, '-')
    .replace(/\s+/g, '-');
  return s.replace(/-+/g, '-').replace(/^-|-$/g, '').slice(0, 80) || 'resume';
}

function triggerBlobDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function OptimizedResumePreviewModal({
  open,
  onClose,
  headline,
  payload,
  error,
  errorSubtitle = "We couldn't load this résumé",
  eyebrow = 'Optimized resume (saved to profile)',
  consultancyWatermark,
  downloadFileBase,
}: Props) {
  const [downloading, setDownloading] = useState(false);
  const showError = Boolean(error?.trim());

  const handleDownloadPdf = useCallback(async () => {
    if (!payload) return;
    const base = sanitizeFileBase(downloadFileBase ?? headline ?? 'resume');
    const filename = `${base}.pdf`;
    const wm = consultancyWatermark?.trim() ?? '';

    setDownloading(true);
    try {
      if (payload.kind === 'text') {
        const sections = buildResumeSectionsFromStoredBody(payload.body);
        if (sections.length === 0) return;

        const blob = await captureResumePreviewAsPdf(sections, payload.templateId, {
          design: payload.design,
          footerWatermark: wm || undefined,
        });
        triggerBlobDownload(blob, filename);
        return;
      }

      const res = await fetch(payload.url);
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      let blob = await res.blob();
      if (wm) {
        try {
          blob = await stampPdfFooterWatermark(blob, wm);
        } catch {
          /* stamp failed (e.g. encrypted PDF) — still offer file */
        }
      }
      triggerBlobDownload(blob, filename);
    } catch (e) {
      console.error('Resume PDF download failed:', e);
    } finally {
      setDownloading(false);
    }
  }, [payload, consultancyWatermark, downloadFileBase, headline]);

  const canDownload = useMemo(() => {
    if (!payload) return false;
    if (payload.kind === 'pdf') return true;
    return buildResumeSectionsFromStoredBody(payload.body).length > 0;
  }, [payload]);

  if (!open) return null;
  if (!showError && !payload) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/45 p-4 sm:p-6 backdrop-blur-[2px]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="resume-preview-title"
      onClick={onClose}
    >
      <div
        className="flex w-full max-w-[min(100%,32rem)] flex-col overflow-hidden rounded-xl border border-[var(--vscode-border-primary)] bg-[var(--vscode-bg-elevated)] shadow-lg shadow-black/15 sm:max-w-[min(100%,36rem)]"
        style={{ maxHeight: 'min(88vh, 720px)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="ggj-resume-preview-header relative flex shrink-0 items-center gap-2 border-b px-3 py-3 sm:gap-3 sm:px-4 sm:py-3.5">
          <span
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/20 text-white [&_svg]:h-[18px] [&_svg]:w-[18px] sm:h-10 sm:w-10 [&_svg]:sm:h-5 [&_svg]:sm:w-5"
            aria-hidden
          >
            <FileText strokeWidth={2.25} />
          </span>
          <div className="min-w-0 flex-1 pr-1">
            <p className="text-[9px] font-semibold uppercase tracking-[0.14em] text-white/85 sm:text-[10px]">{eyebrow}</p>
            <h2 id="resume-preview-title" className="truncate text-sm font-bold leading-snug text-white sm:text-base">
              {headline || 'Résumé preview'}
            </h2>
            {showError ? (
              <p className="mt-0.5 text-[11px] font-medium text-white/90">{errorSubtitle}</p>
            ) : payload?.kind === 'pdf' ? (
              <p className="mt-0.5 text-[11px] text-white/75">PDF document</p>
            ) : payload ? (
              <p className="mt-0.5 text-[11px] text-white/75">Saved template preview</p>
            ) : null}
          </div>
          {!showError ? (
            <button
              type="button"
              onClick={handleDownloadPdf}
              disabled={downloading || !canDownload}
              title={
                canDownload
                  ? 'Download PDF (A4, matches template; footer shows your organisation when set)'
                  : 'Nothing to export'
              }
              className="flex h-9 shrink-0 items-center gap-1.5 rounded-full border border-white/35 bg-white/15 px-2.5 text-[11px] font-semibold text-white transition-colors hover:bg-white/25 disabled:cursor-not-allowed disabled:opacity-40 sm:h-10 sm:px-3.5 sm:text-sm"
            >
              {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
              <span className="hidden sm:inline">Download PDF</span>
              <span className="sm:hidden">PDF</span>
            </button>
          ) : null}
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/15 text-white transition-colors hover:bg-white/25 sm:h-10 sm:w-10"
            aria-label="Close preview"
          >
            <X className="h-4 w-4 sm:h-[18px] sm:w-[18px]" strokeWidth={2.25} />
          </button>
        </header>

        <div className="flex min-h-0 flex-1 flex-col bg-[var(--vscode-bg-tertiary)] px-2 pb-2 pt-0 sm:px-3 sm:pb-3">
          {showError ? (
            <div className="flex min-h-[min(40vh,280px)] flex-1 flex-col items-center justify-center gap-4 overflow-auto rounded-b-lg rounded-t-md border border-[var(--vscode-border-primary)] bg-[var(--vscode-bg-elevated)] px-5 py-8 text-center shadow-sm">
              <div
                className="flex h-[72px] w-[72px] shrink-0 items-center justify-center rounded-2xl border border-[var(--brand-accent-border)] bg-[var(--brand-accent-bg)]"
                aria-hidden
              >
                <AlertCircle className="h-10 w-10 text-amber-500" strokeWidth={2} />
              </div>
              <div className="max-w-md space-y-2">
                <p className="text-xs font-semibold uppercase tracking-[0.12em] vscode-text-tertiary">Something went wrong</p>
                <p className="text-sm leading-relaxed vscode-text-secondary">{error}</p>
              </div>
            </div>
          ) : payload?.kind === 'pdf' ? (
            <div className="flex min-h-[min(42vh,320px)] flex-1 flex-col overflow-hidden rounded-b-lg rounded-t-md bg-white shadow-sm ring-1 ring-black/[0.06] dark:bg-[#fafafa]">
              <iframe
                title="Resume PDF preview"
                src={payload.url}
                className="h-[min(58vh,480px)] min-h-[240px] w-full flex-1 rounded-b-lg rounded-t-md bg-white"
              />
            </div>
          ) : payload ? (
            <div className="flex min-h-[min(42vh,320px)] flex-1 flex-col overflow-hidden rounded-b-lg rounded-t-md bg-white shadow-sm ring-1 ring-black/[0.06] dark:bg-[#fafafa]">
              <div className="min-h-0 max-h-[min(58vh,480px)] flex-1 overflow-y-auto overflow-x-auto overscroll-contain px-2 py-2.5 sm:px-3 sm:py-3">
                <SavedOptimizedResumePreview
                  bodyText={payload.body}
                  templateId={payload.templateId}
                  design={payload.design}
                />
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
